import { IconCalendarEvent } from '@tabler/icons-react'
import { TableColumnType } from 'antd'
import moment from 'moment'

import {
  DateTimeDisplayBlock as RenderDateTimeDisplayBlock,
  renderTimezonedDateBlock,
} from '../../templateRenderer/DisplayBlocks/DateTimeDisplayBlock'
import { DateTimeDisplayBlock } from '../../types/blueprint'

import {
  BaseBlockConfig,
  ColumnOptions,
  CsvColumn,
  CsvColumnOptions,
  Field,
  FORBIDDEN_IN_APP,
  InputField,
  SwitchField,
  TypeOption,
} from './_BaseBlockConfig'

import { Text } from '@/components/shared-components/BUI/Text'
import { TimezonedDateProps } from '@/context/timezoneContext'
import antdTableSorter from '@/utils/antdTableSorter'

class Config extends BaseBlockConfig<DateTimeDisplayBlock> {
  public override kind = 'display' as const
  public override type = 'dateTime' as const

  public override enableAdminOnlyVisiblity = true
  public override enableBlockToCsvColumn = true
  public override enableBlockToColumnDisplay = true

  public override blockTypeConfig: TypeOption = {
    kind: this.kind,
    value: this.type,
    label: 'Date / time',
    icon: IconCalendarEvent,
    helpText: 'Show a nicely formatted date or time',
    hideDescription: true,
    forbiddenTemplateTypes: FORBIDDEN_IN_APP,
  }

  public override blockFieldsConfig: Field[] = [
    {
      name: 'content',
      label: 'Date / Time',
      field: InputField,
      helpText: 'e.g. Apr 30 1992 07:23',
      required: true,
    },
    {
      name: 'showTime',
      label: 'Show time',
      field: SwitchField,
    },
  ]

  public override mapFormValueToBlock(
    block: DateTimeDisplayBlock
  ): DateTimeDisplayBlock {
    return block
  }

  public override mapBlockToColumnDisplay(
    block: DateTimeDisplayBlock,
    options: ColumnOptions
  ): React.ReactNode {
    const { captureRange, content, pickerType } = block

    const overdue =
      content && !Array.isArray(content)
        ? moment().isAfter(moment(content))
        : false

    if (options.assertOverdue && overdue) {
      return (
        <Text semantic="errorDark" strong>
          {renderTimezonedDateBlock(captureRange, content, pickerType, false)}
        </Text>
      )
    }

    return renderTimezonedDateBlock(captureRange, content, pickerType, false)
  }

  public override mapBlockToCsvColumn(
    title: string,
    block: DateTimeDisplayBlock
  ): CsvColumn[] {
    if (block.captureRange) return super.mapBlockToCsvColumn(title, block)

    return [
      {
        blockId: block.id,
        title: `${title} - Start`,
        originalBlockName: block.name,
        options: { dateRangeIndex: 0 },
        type: 'dateTime',
      },
      {
        blockId: block.id,
        title: `${title} - End`,
        originalBlockName: block.name,
        options: { dateRangeIndex: 1 },
        type: 'dateTime',
      },
    ]
  }

  public override mapBlockToCsvValue(
    block: DateTimeDisplayBlock,
    options: CsvColumnOptions | undefined,
    formatDateString: (props: TimezonedDateProps) => string
  ): string | null {
    return formatDateString({
      date: Array.isArray(block.content)
        ? block.content[options?.dateRangeIndex ?? 0]
        : block.content,
      includeDate: block.pickerType !== 'time',
      includeTime:
        block.pickerType === 'dateTime' || block.pickerType === 'time',
    })
  }

  public override mapBlockToColumnSort(
    block: DateTimeDisplayBlock,
    enableDefaultSorting?: boolean
  ): TableColumnType<unknown> {
    return {
      defaultSortOrder: enableDefaultSorting ? 'descend' : undefined,
      sorter: (a, b) => {
        const [aBlock, bBlock] = this.getAandBBlocks(block, a, b)

        if (!aBlock || !bBlock) return 0

        if (aBlock.kind === 'display' && bBlock.kind === 'display') {
          return antdTableSorter(aBlock, bBlock, 'content')
        }

        return 0
      },
    }
  }

  public override renderBlock(block: DateTimeDisplayBlock): React.ReactNode {
    return <RenderDateTimeDisplayBlock block={block} />
  }
}

export const DateTimeDisplayBlockConfig = new Config()
